import { useLazyLoadQuery } from 'react-relay';
import GET_POST_BY_ID_QUERY, { type GetPostByIdQuery } from '../../graphql/__generated__/GetPostByIdQuery.graphql';
import type { PostDetailData } from 'typings/posts';

const usePostById = (id: string) => {
  const postByIdData = useLazyLoadQuery<GetPostByIdQuery>(
    GET_POST_BY_ID_QUERY,
    { id },
    {
      fetchPolicy: 'store-or-network',
    },
  );

  const results = postByIdData?.unified?.ai?.getPostById;

  if (results?.__typename === 'UnifiedAiPostResult') {
    const postDetailData: PostDetailData = normalizePostForDetail(results.post);
    return {
      results,
      postDetailData,
    };
  }

  return {
    results: null,
    postDetailData: null,
  };
};

export default usePostById;

// Helper function to format time ago
const formatTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else {
    return `${diffInDays}d ago`;
  }
};

// Helper function to generate avatar initials and color
const generateAvatar = (name: string) => {
  if (!name || typeof name !== 'string') {
    return { initials: 'U', backgroundColor: '#ff8b00' };
  }

  const initials = name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Simple hash function to generate consistent colors
  const hash = name.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  const colors = [
    '#ff8b00',
    '#0052cc',
    '#36b37e',
    '#ff5630',
    '#6554c0',
    '#ff7452',
    '#00b8d9',
    '#57d9a3',
    '#ff8b00',
    '#de350b',
  ];

  const backgroundColor = colors[Math.abs(hash) % colors.length];

  return { initials, backgroundColor };
};

const normalizePostForDetail = (post: any): PostDetailData => {
  const authorName = post.author?.nickname || 'Unknown Author';
  const avatar = generateAvatar(authorName);

  return {
    id: post.id,
    title: post.title || 'Untitled',
    author: {
      avatar,
      name: authorName,
      badge: post.author?.org_id ? 'ATLASSIAN TEAM' : "I'M NEW HERE",
      timeAgo: formatTimeAgo(post.created_at || new Date().toISOString()),
    },
    content: post.content,
    tags: post.tags || [],
    actions: {
      views: 0, // Not available in current GraphQL response
      likesInfo: {
        count: post.like_ids?.length || 0,
        likedBy: post.like_ids?.length > 0 ? 'Someone' : '',
      },
    },
    responses: post.responses || [],
  };
};
